import React, { type PropsWithChildren } from "react";
import { SimpleGrid } from "@chakra-ui/react";
import { Section } from "components/section";
import { TextColumn, TextTitle } from "ui/text";
import {
  CodeIcon,
  ExperimentsIcon,
  HeartIcon,
  LeversIcon,
  MailIcon,
  MegaphoneIcon,
  PersonWithCheckIcon,
  SparklesIcon,
  TrendIcon,
} from "ui/icons";
import { FilterTabs } from "ui/tabs";

function TabSection({ children }: PropsWithChildren) {
  return (
    <SimpleGrid columns={{ base: 1, lg: 3 }} gap={{ base: "biggest", md: "big" }}>
      {children}
    </SimpleGrid>
  );
}

function FirstTabSection() {
  return (
    <TabSection>
      <TextColumn
        iconChip={{ variant: "red", icon: HeartIcon }}
        title="Build a personalized loyalty program"
        description="Use data to create loyalty programs that work. Deliver the best offers and most relevant messages to every customer."
        cta={{ href: "/blog/loyalty-programs" }}
      />
      <TextColumn
        iconChip={{ variant: "yellow", icon: MailIcon }}
        title="Personalize messages with product catalogs"
        description="Always recommend the next-best-action by leveraging all of your organization’s data including data science and product catalogs."
        cta={{ href: "/platform/ai-decisioning" }}
      />

      <TextColumn
        iconChip={{ variant: "blue", icon: PersonWithCheckIcon }}
        title="Power omni-channel campaigns"
        description="Give your customers a consistent, effective, and delightful experience across every channel by centrally managing your audience logic."
        cta={{ href: "/platform/customer-studio" }}
      />
    </TabSection>
  );
}

function SecondTabSection() {
  return (
    <TabSection>
      <TextColumn
        iconChip={{ variant: "violet", icon: TrendIcon }}
        title="Calibrate bids for new vs existing customers to maximize financial outcomes"
        description="Align your bidding strategy with business value by setting different CPA and ROAS targets across cohorts."
        cta={{
          href: "/blog/last-minute-holiday-paid-media-strategies#2-increase-reach-across-repeat-purchase-campaigns",
        }}
      />
      <TextColumn
        iconChip={{ variant: "green", icon: CodeIcon }}
        title="Increase ROAS by switching from pixels to conversion APIs"
        description="Enrich conversion events with first-party data through conversion APIs for higher match rates that drive better AI outcomes."
        cta={{ href: "/playbooks/send-better-signals-with-facebook-conversion-api" }}
      />
      <TextColumn
        iconChip={{ variant: "yellow", icon: MegaphoneIcon }}
        title="Deliver relevant remarketing with product catalogs"
        description="Retarget customers with instantly relevant products based on recent page visits, searches, and abandoned carts."
        cta={{ href: "/playbooks/sync-product-catalogs-to-facebook" }}
      />
    </TabSection>
  );
}

function ThirdTabSection() {
  return (
    <TabSection>
      <TextColumn
        iconChip={{ variant: "blue", icon: LeversIcon }}
        title="Understand the true lift of every channel with holdout testing"
        description="Understand incrementally of email and advertising programs with audience level hold-out testing."
        cta={{ href: "/platform/customer-studio#splits" }}
      />
      <TextColumn
        iconChip={{ variant: "violet", icon: SparklesIcon }}
        title="Automate experimentation with AI Decisioning"
        description="Move away from manual testing and one-off insights. Power 1-1 experimentation at a user level with AI."
        cta={{ href: "/platform/ai-decisioning" }}
      />
      <TextColumn
        iconChip={{ variant: "green", icon: ExperimentsIcon }}
        title="Close the loop with self-serve campaign analytics"
        description="Measure, explore, and analyze your campaign performance, all on your own. Take action on your insights with AB experiments and incrementally tests."
        cta={{ href: "/platform/campaign-intelligence" }}
      />
    </TabSection>
  );
}

export function RetailTabs() {
  return (
    <Section>
      <TextTitle
        size="lg"
        align="center"
        title="Better access to data for marketers, better experiences for customers"
      />

      <FilterTabs
        mt="bigger"
        tabs={[
          {
            label: "Lifecycle marketing",
            items: <FirstTabSection />,
          },
          {
            label: "Paid marketing",
            items: <SecondTabSection />,
          },
          {
            label: "Experimentation",
            items: <ThirdTabSection />,
          },
        ]}
      />
    </Section>
  );
}
